import React, { useState } from "react";
import { useNavigate } from "react-router-dom";
import { toast } from "../utils/toast";
import { useAuth } from "../contexts/AuthContext";
import { normalizeIndianMobileNumber } from "../utils/phoneNumber";
import "../styles/shared/LoadingState.css";
import "../styles/pages/MobileMarketplace.css";
import { MobilePageHeader } from "../components/layout/MobileAppChrome";

const restrictLettersOnly = (value: string) => value.replace(/[^A-Za-z\s.'-]/g, "");

const EditProfilePage: React.FC = () => {
  const { completeProfile, user } = useAuth();
  const navigate = useNavigate();
  const [fullName, setFullName] = useState(user?.fullName || "");
  const [phoneNumber, setPhoneNumber] = useState(user?.phoneNumber || "");
  const [email, setEmail] = useState(user?.email || "");
  const [saving, setSaving] = useState(false);
  const [popupMessage, setPopupMessage] = useState("");

  const handleSubmit = async (event: React.FormEvent<HTMLFormElement>) => {
    event.preventDefault();
    if (!fullName.trim() || !phoneNumber.trim()) {
      toast.error("Full name and mobile number are required.");
      return;
    }

    setSaving(true);
    setPopupMessage("");
    const result = await completeProfile({
      fullName: fullName.trim(),
      phoneNumber: normalizeIndianMobileNumber(phoneNumber),
      email: email.trim() || undefined,
    });
    setSaving(false);

    if (result.error) {
      setPopupMessage(result.error);
      toast.error(result.error);
      return;
    }

    toast.success("Profile updated.");
    navigate("/profile", { replace: true, state: { openAccountMenu: true } });
  };

  return (
    <section className="shell section page-section edit-profile-page">
      <div className="edit-profile-mobile-view">
        <MobilePageHeader title="Edit Profile" />
      </div>
      <div className="page-header">
        <span className="eyebrow">Profile</span>
        <h1>Edit profile</h1>
      </div>

      {popupMessage ? (
        <div
          role="alert"
          aria-live="assertive"
          className="store-card"
          style={{
            border: "1px solid #111",
            background: "#fff",
            color: "#111",
            boxShadow: "0 18px 34px rgba(17, 17, 17, 0.14)",
            marginBottom: "12px",
            padding: "14px 16px",
          }}
        >
          <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", gap: "12px" }}>
            <strong>{popupMessage}</strong>
            <button
              type="button"
              className="link-button"
              onClick={() => setPopupMessage("")}
              style={{ minHeight: "36px", padding: "0 12px", border: "1px solid #111", color: "#111" }}
            >
              Close
            </button>
          </div>
        </div>
      ) : null}

      <div className="checkout-layout">
        <section className="store-card edit-profile-desktop-profile-card">
          <div className="edit-profile-desktop-profile-card__header">
            <span className="eyebrow">Saved profile</span>
          </div>
          <div className="edit-profile-desktop-profile-card__body">
            <div className="edit-profile-desktop-profile-card__avatar" aria-hidden="true">
              <span>{(fullName || user?.email || "E").slice(0, 1).toUpperCase()}</span>
            </div>
            <div className="edit-profile-desktop-profile-card__details">
              <strong>{fullName || "Eldoo Customer"}</strong>
              <p>{phoneNumber || "Mobile number missing"}</p>
              <small>{email || "Email not added yet"}</small>
            </div>
            <div className="edit-profile-desktop-profile-card__meta">
              <span className="edit-profile-desktop-profile-card__meta-label">Profile status</span>
              <strong>{fullName && phoneNumber ? "Saved" : "Needs update"}</strong>
            </div>
          </div>
        </section>

        <form className="store-card form-card" onSubmit={handleSubmit}>
          <h2>Profile details</h2>
          <div className="form-grid">
            <label>
              Full name
              <input
                value={fullName}
                onChange={(event) => setFullName(restrictLettersOnly(event.target.value))}
                onPaste={(event) => {
                  event.preventDefault();
                  setFullName(restrictLettersOnly(event.clipboardData.getData("text")));
                }}
                inputMode="text"
                pattern={"[A-Za-z\\s.'\\-]+"}
                required
              />
            </label>
            <label>
              Mobile number
              <input
                value={phoneNumber}
                onChange={(event) => setPhoneNumber(normalizeIndianMobileNumber(event.target.value))}
                onPaste={(event) => {
                  event.preventDefault();
                  setPhoneNumber(normalizeIndianMobileNumber(event.clipboardData.getData("text")));
                }}
                inputMode="numeric"
                pattern={"\\d{10}"}
                maxLength={10}
                required
              />
            </label>
            <label>
              Email optional
              <input type="email" value={email} onChange={(event) => setEmail(event.target.value)} />
            </label>
          </div>
          <button className="button" type="submit" disabled={saving}>
            {saving ? (
              <span className="button-loading">
                <span className="button-loading__spinner" aria-hidden="true" />
                Saving...
              </span>
            ) : (
              "Save profile"
            )}
          </button>
        </form>
      </div>
    </section>
  );
};

export default EditProfilePage;
